import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import LabelWithInfo from './shared/LabelWithInfo';
import { Metric, MetricTarget } from '../types';
import { targetsApi, metricsApi } from '../services/api';
import { extractApiError } from '../types/apiError';

const KPI_TARGETS_STORAGE_KEY = 'kpi-targets-by-quarter';

type StoredTargetPayload = {
  target_value: number;
  target_type: 'total' | 'threshold';
  threshold_direction: 'below' | 'above';
  evaluation_mode: 'weekly' | 'rolling';
};

function getStoredTargetsForQuarter(quarter: number, year: number): Record<string, StoredTargetPayload> {
  try {
    const raw = localStorage.getItem(KPI_TARGETS_STORAGE_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw) as Record<string, Record<string, StoredTargetPayload>>;
    const key = `${year}-Q${quarter}`;
    return data[key] || {};
  } catch {
    return {};
  }
}

function setStoredTargetForQuarter(
  quarter: number,
  year: number,
  metricId: number,
  payload: StoredTargetPayload
): void {
  try {
    const raw = localStorage.getItem(KPI_TARGETS_STORAGE_KEY);
    const data = raw ? JSON.parse(raw) : {};
    const key = `${year}-Q${quarter}`;
    if (!data[key]) data[key] = {};
    data[key][String(metricId)] = payload;
    localStorage.setItem(KPI_TARGETS_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to save KPI target to localStorage', e);
  }
}

function getCurrentQuarterAndYear(): { quarter: number; year: number } {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  let quarter = 1;
  if (month >= 9) quarter = 4;
  else if (month >= 6) quarter = 3;
  else if (month >= 3) quarter = 2;
  return { quarter, year };
}

interface TargetFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  metric: Metric | null;
}

const TargetForm: React.FC<TargetFormProps> = ({ open, onClose, onSuccess, metric: propMetric }) => {
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [selectedMetricId, setSelectedMetricId] = useState<number | ''>('');
  const [targetValue, setTargetValue] = useState('');
  const [quarter, setQuarter] = useState<number>(1);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [targetType, setTargetType] = useState<'total' | 'threshold'>('total');
  const [thresholdDirection, setThresholdDirection] = useState<'below' | 'above'>('below');
  const [evaluationMode, setEvaluationMode] = useState<'weekly' | 'rolling'>('weekly');
  const [targetsForQuarter, setTargetsForQuarter] = useState<MetricTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editOtherOpen, setEditOtherOpen] = useState(false);
  const [pickQuarter, setPickQuarter] = useState<number>(1);
  const [pickYear, setPickYear] = useState<number>(new Date().getFullYear());

  const loadTargetsForQuarter = useCallback(async (q: number, y: number) => {
    try {
      const list = await targetsApi.getForQuarter(q, y);
      setTargetsForQuarter(list);
    } catch (e) {
      console.error('Failed to load targets for quarter', e);
      setTargetsForQuarter([]);
    }
  }, []);

  // When dialog opens: default to current quarter/year, load metrics; targets load in the quarter/year effect below
  useEffect(() => {
    if (open) {
      loadMetrics();
      const { quarter: q, year: y } = getCurrentQuarterAndYear();
      setQuarter(q);
      setYear(y);
      setTargetValue('');
      setTargetType('total');
      setThresholdDirection('below');
      setEvaluationMode('weekly');
      setError(null);
      setEditOtherOpen(false);
      setPickQuarter(q);
      setPickYear(y);
      if (propMetric) {
        setSelectedMetricId(propMetric.id);
      } else {
        setSelectedMetricId('');
      }
    }
  }, [open, propMetric]);

  // When quarter/year change (e.g. user selects different quarter or loaded from "Edit other quarter"), reload targets
  useEffect(() => {
    if (open && quarter && year) {
      loadTargetsForQuarter(quarter, year);
    }
  }, [open, quarter, year, loadTargetsForQuarter]);

  // Pre-fill form when metric or targets for this quarter change (from API or localStorage)
  useEffect(() => {
    if (!open || selectedMetricId === '') return;
    const mid = selectedMetricId as number;
    const fromApi = targetsForQuarter.find((t) => t.metric_id === mid);
    if (fromApi) {
      setTargetValue(String(fromApi.target_value));
      setTargetType(fromApi.target_type);
      setThresholdDirection(fromApi.threshold_direction);
      setEvaluationMode(fromApi.evaluation_mode);
      return;
    }
    const stored = getStoredTargetsForQuarter(quarter, year)[String(mid)];
    if (stored) {
      setTargetValue(String(stored.target_value));
      setTargetType(stored.target_type);
      setThresholdDirection(stored.threshold_direction);
      setEvaluationMode(stored.evaluation_mode);
      return;
    }
    setTargetValue('');
    setTargetType('total');
    setThresholdDirection('below');
    setEvaluationMode('weekly');
  }, [open, selectedMetricId, quarter, year, targetsForQuarter]);

  const loadMetrics = async () => {
    try {
      const data = await metricsApi.getAll();
      setMetrics(data);
    } catch (error) {
      console.error('Failed to load metrics:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMetricId) {
      setError('Please select a metric');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const value = parseFloat(targetValue);
      await targetsApi.set(selectedMetricId as number, value, quarter, year, {
        target_type: targetType,
        threshold_direction: thresholdDirection,
        evaluation_mode: evaluationMode,
      });
      setStoredTargetForQuarter(quarter, year, selectedMetricId as number, {
        target_value: value,
        target_type: targetType,
        threshold_direction: thresholdDirection,
        evaluation_mode: evaluationMode,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(extractApiError(err, 'Failed to set target'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditOtherQuarterLoad = () => {
    setQuarter(pickQuarter);
    setYear(pickYear);
    setEditOtherOpen(false);
    // Effect above will run and load targets for the new quarter/year
  };

  const selectedMetric = metrics.find((m) => m.id === selectedMetricId);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  const yearsWide = Array.from({ length: 13 }, (_, i) => currentYear - 10 + i);

  const renderLabel = (label: string, tooltip: string) => (
    <LabelWithInfo label={label} tooltip={tooltip} />
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Set Quarterly Target</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography component="span" variant="body2" color="text.secondary">
                  Defaults to current quarter. Use the button to load another quarter&apos;s saved targets.
                </Typography>
                <Button
                  size="small"
                  startIcon={<EditCalendarIcon />}
                  onClick={() => setEditOtherOpen(true)}
                  variant="outlined"
                >
                  Edit another quarter
                </Button>
              </Box>
              <FormControl fullWidth required>
                <InputLabel>Metric</InputLabel>
                <Select
                  value={selectedMetricId}
                  label="Metric"
                  onChange={(e) => setSelectedMetricId(e.target.value as number | '')}
                >
                  {metrics.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={renderLabel(
                  'Target Value',
                  'Total: 24 for the quarter. Threshold: 3 per week.'
                )}
                type="number"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                required
                fullWidth
                inputProps={{ step: 'any', min: 0 }}
                helperText={
                  targetType === 'threshold'
                    ? 'Weekly threshold (e.g., 3 late tickets per week)'
                    : selectedMetric?.unit
                      ? `Total for the quarter in ${selectedMetric.unit} (e.g., 24)`
                      : 'Total for the quarter (e.g., 24)'
                }
              />
              <FormControl fullWidth>
                <InputLabel>
                  {renderLabel(
                    'Target Type',
                    'Total = quarter total (e.g., 24 leads). Threshold = weekly cap/floor (e.g., ≤ 3 late tickets).'
                  )}
                </InputLabel>
                <Select
                  value={targetType}
                  label="Target Type"
                  onChange={(e) => setTargetType(e.target.value as 'total' | 'threshold')}
                >
                  <MenuItem value="total">Total (Quarter)</MenuItem>
                  <MenuItem value="threshold">Threshold (Weekly)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>
                  {renderLabel(
                    'Threshold Expectation',
                    'Stay Below = keep weekly values under target. Stay Above = hit or exceed target weekly.'
                  )}
                </InputLabel>
                <Select
                  value={thresholdDirection}
                  label="Threshold Expectation"
                  onChange={(e) => setThresholdDirection(e.target.value as 'below' | 'above')}
                  disabled={targetType !== 'threshold'}
                >
                  <MenuItem value="below">Stay Below Target (e.g., ≤ 3)</MenuItem>
                  <MenuItem value="above">Stay Above Target (e.g., ≥ 30)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>
                  {renderLabel(
                    'Evaluation Mode',
                    'Weekly = compare each week. Rolling Average = compare the average to date.'
                  )}
                </InputLabel>
                <Select
                  value={evaluationMode}
                  label="Evaluation Mode"
                  onChange={(e) => setEvaluationMode(e.target.value as 'weekly' | 'rolling')}
                >
                  <MenuItem value="weekly">Weekly (compare each week)</MenuItem>
                  <MenuItem value="rolling">Rolling Average (to date)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Quarter</InputLabel>
                <Select
                  value={quarter}
                  label="Quarter"
                  onChange={(e) => setQuarter(e.target.value as number)}
                >
                  <MenuItem value={1}>Q1 (Jan - Mar)</MenuItem>
                  <MenuItem value={2}>Q2 (Apr - Jun)</MenuItem>
                  <MenuItem value={3}>Q3 (Jul - Sep)</MenuItem>
                  <MenuItem value={4}>Q4 (Oct - Dec)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Year</InputLabel>
                <Select
                  value={year}
                  label="Year"
                  onChange={(e) => setYear(e.target.value as number)}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {error && <Alert severity="error">{error}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={loading || !targetValue}>
              Set Target
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={editOtherOpen} onClose={() => setEditOtherOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit another quarter</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Quarter</InputLabel>
              <Select
                value={pickQuarter}
                label="Quarter"
                onChange={(e) => setPickQuarter(e.target.value as number)}
              >
                <MenuItem value={1}>Q1 (Jan - Mar)</MenuItem>
                <MenuItem value={2}>Q2 (Apr - Jun)</MenuItem>
                <MenuItem value={3}>Q3 (Jul - Sep)</MenuItem>
                <MenuItem value={4}>Q4 (Oct - Dec)</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={pickYear}
                label="Year"
                onChange={(e) => setPickYear(e.target.value as number)}
              >
                {yearsWide.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOtherOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditOtherQuarterLoad}>
            Load quarter
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TargetForm;
